import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./EstoreComponent.css";
import { useLocation, useNavigate } from "react-router-dom";
import es1 from "../assets/es1.png";
import es2 from "../assets/es2.png";
import es3 from "../assets/es3.png";
import es4 from "../assets/es4.png";
import es5 from "../assets/es5.png";
import es6 from "../assets/es6.png";
import es7 from "../assets/es7.png";
import es8 from "../assets/es8.png";
import es9 from "../assets/es9.png";
import es10 from "../assets/es10.png";
import es11 from "../assets/es11.png";
import { API_BASE_URL } from "../config/api";
import { resolveImageUrl } from "../utils/image";

const PRODUCTS_ENDPOINT = `${API_BASE_URL}/api/products`;
const PRODUCTS_CACHE_KEY = "estore_products_cache_v1";

const categoryImages = {
  "Glamur Paints": es1,
  "Dulux Paints": es2,
  "Corona Paints": es3,
  "Diamond Paints": es4,
  "Nelson Paints": es5,
  "Gobi's Paints": es6,
  "Berger Paints": es7,
  "Kansai Paints": es8,
  "Brighto Paints": es9,
  "Reliance Paints": es10,
  "Reliable Paints": es11,
};

export default function EstoreComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    const cachedProducts = localStorage.getItem(PRODUCTS_CACHE_KEY);
    if (cachedProducts) {
      try {
        const parsed = JSON.parse(cachedProducts);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setProducts(parsed);
          setIsLoading(false);
        }
      } catch (error) {
        localStorage.removeItem(PRODUCTS_CACHE_KEY);
      }
    }

    const fetchProducts = async () => {
      try {
        const res = await axios.get(PRODUCTS_ENDPOINT, { timeout: 10000 });
        const fetchedProducts = Array.isArray(res.data) ? res.data : [];

        if (!isMounted) return;

        setProducts(fetchedProducts);
        setErrorMessage("");
        localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(fetchedProducts));
      } catch (error) {
        if (!isMounted) return;

        if (!cachedProducts) {
          setErrorMessage("Products are not available right now. Please try again later.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  const categories = useMemo(
    () => [...new Set(products.map((p) => p.category).filter(Boolean))],
    [products]
  );

  useEffect(() => {
    const queryCategory = new URLSearchParams(location.search).get("category");
    if (!queryCategory) return;

    const normalizedQuery = queryCategory.trim().toLowerCase();
    const matchedCategory = categories.find(
      (category) => category.toLowerCase() === normalizedQuery
    );

    if (matchedCategory && matchedCategory !== selectedCategory) {
      setSelectedCategory(matchedCategory);
    }
  }, [location.search, categories, selectedCategory]);

  const filteredProducts = useMemo(
    () =>
      selectedCategory
        ? products.filter((p) => p.category === selectedCategory)
        : [],
    [products, selectedCategory]
  );

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    if (location.search) {
      navigate("/estore", { replace: true });
    }
  };

  return (
    <div className="estore-container">
      <h2 className="estore-title">OUR PRODUCTS</h2>
      <p className="estore-subtitle">
        RUNGDO brings together Pakistan's most trusted paint brands in one
        place, offering unmatched quality, rich color durability, and the
        perfect finish for every residential or commercial project. Whether
        you're upgrading a single room or taking on a large-scale renovation,
        we have the right products to bring your vision to life.
      </p>

      {isLoading && products.length === 0 && (
        <p className="estore-status">Products loading...</p>
      )}

      {errorMessage && products.length === 0 && (
        <p className="estore-error">{errorMessage}</p>
      )}

      {!selectedCategory && categories.length > 0 && (
        <div className="categories-grid">
          {categories.map((cat) => (
            <div
              key={cat}
              className="category-card"
              onClick={() => setSelectedCategory(cat)}
            >
              <img
                src={categoryImages[cat]}
                alt={cat}
                className="category-img"
                loading="lazy"
              />
              <h3>{cat}</h3>
            </div>
          ))}
        </div>
      )}

      {!isLoading && !selectedCategory && categories.length === 0 && !errorMessage && (
        <p className="estore-status">No products available.</p>
      )}

      {selectedCategory && (
        <div>
          <button
            onClick={handleBackToCategories}
            className="back-button"
          >
            Back to Categories
          </button>
          <h3>{selectedCategory}</h3>
          <div className="estore-products-grid">
            {filteredProducts.map((item, idx) => (
              <div
                key={item._id || item.slug || `${selectedCategory}-${idx}`}
                className="estore-card"
                onClick={() => navigate(`/product/${item.slug}`)}
              >
                {item.image && (
                  <img
                    src={resolveImageUrl(item.image)}
                    alt={item.name}
                    className="estore-img"
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <h4 className="estore-name">{item.name}</h4>
                <p className="estore-desc">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
